import { useCallback, useEffect, useRef } from 'react'

import { useFormikContext } from 'formik'

import { Collapse, Divider } from '@mui/material'
import { ErrorOutline, RestartAlt, Save } from '@mui/icons-material'

import { useSelector } from '@tabeeb/store'
import { useFormContext } from '@collaborate-health/forms'
import { Button, Caption, Link2, Stack, Tooltip } from '@collaborate-health/uikit'

import { getIsAnswerInProgress } from '../../selectors'

const SubmitFormButton: React.FC = () => {
  const isAnswerInProgress = useSelector(getIsAnswerInProgress)

  const { form } = useFormContext()
  const formRef = useRef(form)

  const { isSubmitting, errors, dirty, isValid } = useFormikContext<Record<string, unknown>>()

  const controlIdToNameMap = new Map<string, string>()
  form?.elements.forEach((el) => controlIdToNameMap.set(el.id, el.name!))

  const onScrollToControl = useCallback((controlId: string) => {
    const element = document.getElementById(`form-question-${controlId}`)
    element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [])

  useEffect(() => {
    if (!isSubmitting) {
      return
    }

    const controlsWithErrorsIdsSet = new Set(Object.keys(errors))
    if (controlsWithErrorsIdsSet.size <= 0) {
      return
    }

    let minOrder = Infinity
    let controlWithMinOrderId = null

    for (const control of formRef.current?.elements ?? []) {
      if (controlsWithErrorsIdsSet.has(control.id) && control.order < minOrder) {
        minOrder = control.order
        controlWithMinOrderId = control.id
      }
    }

    if (controlWithMinOrderId) {
      onScrollToControl(controlWithMinOrderId)
    }
  }, [errors, isSubmitting, onScrollToControl])

  return (
    <Stack position='sticky' bottom={0}>
      <Collapse in={dirty || isAnswerInProgress}>
        <Stack bgcolor='background.paper'>
          <Divider />

          <Stack direction='row' spacing={1} paddingBlock={1} paddingInline={1.5} justifyContent='space-evenly'>
            <Stack width='100%'>
              <Button
                size='small'
                startIcon={<RestartAlt />}
                fullWidth
                disabled={isSubmitting || !dirty || isAnswerInProgress}
                type='reset'
                color='primary'
                variant='outlined'
              >
                Undo
              </Button>
            </Stack>

            <Tooltip
              cursor='help'
              title={isValid ? null : 'Errors'}
              text={
                isValid ? null : (
                  <Stack spacing={1} minWidth={0} overflow='hidden'>
                    {Object.entries(errors).map(([key, error]) => (
                      <Stack
                        key={key}
                        direction='row'
                        alignItems='center'
                        gap={1}
                        onClick={() => onScrollToControl(key)}
                      >
                        <ErrorOutline color='error' />
                        <Stack overflow='hidden' minWidth={0}>
                          <Link2 color='textPrimary' fontWeight={500} title={controlIdToNameMap.get(key)} noWrap>
                            {controlIdToNameMap.get(key)}
                          </Link2>
                          <Caption title={error}>{error}</Caption>
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                )
              }
            >
              <Stack width='100%'>
                <Button
                  size='small'
                  startIcon={<Save />}
                  loading={isSubmitting || isAnswerInProgress}
                  fullWidth
                  disabled={!dirty}
                  type='submit'
                  color='primary'
                  variant='contained'
                  formNoValidate
                >
                  Save
                </Button>
              </Stack>
            </Tooltip>
          </Stack>
        </Stack>
      </Collapse>
    </Stack>
  )
}

export default SubmitFormButton

